import { gql, useQuery } from '@apollo/client'
import { useEffect, useState, useMemo } from 'react';

const GET_CART_DATA = gql`
    query getCart {
  cart {
    id
    programId
    courseId
    course {
      id
      price
      openedxCourseId
      _CourseOverviewsCourseoverview {
        courseImageUrl
        displayName
        id
        shortDescription
      }
    }
    program {
      price
      name
      image
      id
    }
    createdAt
    userId
    status
    organisationId
  }
}
`

/**
 * Hook to fetch and manage cart data from the API
 * @returns {Object} Cart data and related functions
 * @property {Array} data - Raw cart data from the API
 * @property {boolean} loading - Whether the cart data is being loaded
 * @property {Object} error - Any error that occurred during loading
 * @property {Function} refetch - Function to refetch cart data from the API
 * @property {Array} draftCartData - Cart items with status 'draft' or 'pending'
 * @property {Array} wishlistCartData - Cart items with status 'wishlist'
 */
export const useCart = () => {
  const [draftCartData, setDraftCartData] = useState([])
  const [wishlistCartData, setWishlistCartData] = useState([])
  const { data, loading, error, refetch } = useQuery(GET_CART_DATA, {
    context: {
      headers: {
        'x-hasura-role': 'learner',
      },
    }
  }
  )
  useEffect(() => {
    if (data?.cart) {
      setDraftCartData(data.cart.filter(item => item.status === 'draft' || item.status === 'pending'))
      setWishlistCartData(data.cart.filter(item => item.status === 'wishlist'))
    }
  }, [data])
  return { data, loading, error, refetch, draftCartData, wishlistCartData }
}

/**
 * Hook to get formatted cart data for display
 * @returns {Object} Formatted cart data and refetch function
 * @property {Array} parsedCartData - Formatted cart data with consistent structure
 * @property {Function} cartRefetch - Function to refetch cart data
 */
export const useCartData = () => {
  const { draftCartData: cartData, refetch: cartRefetch } = useCart();

  const parsedCartData = useMemo(() => {
    return cartData?.map(item => {
      if (item.course && item.course._CourseOverviewsCourseoverview) {
        return {
          image: item.course._CourseOverviewsCourseoverview.courseImageUrl,
          name: item.course._CourseOverviewsCourseoverview.displayName,
          description: item.course._CourseOverviewsCourseoverview.shortDescription,
          price: item.course.price,
          type: 'course',
          id: item.id
        }
      } else if (item.program) {
        return {
          image: item.program.image,
          name: item.program.name,
          description: item.program.description,
          price: item.program.price,
          type: 'program',
          id: item.id
        }
      }
    })
  }, [cartData]);

  return { parsedCartData, cartRefetch };
}; 